/* ZOOPLICITY Pet Fit V1
   Active pet -> positive-only product-fit badges, collection/search filter, PDP context.
   Uses only structured product metadata already stored in Shopify.
   Never infers ingredient safety from product names or flavours.
*/
(() => {
  'use strict';
  if (window.__ZOOPLICITY_PET_FIT_V1__) return;
  window.__ZOOPLICITY_PET_FIT_V1__ = true;

  const FILTER_KEY = 'zp-pet-fit-only-v1';
  const PET_EVENT = 'zooplicity:pet-profile-changed';
  const CARD_SELECTOR = 'product-card[data-product-id]';
  const GRID_ITEM_SELECTOR = '.product-grid__item';
  const META_SELECTOR = '[data-zp-product-profile-meta]';
  const MANAGE_PAGE_SELECTOR = '[data-zp-pet-center-page]';

  const state = {
    pet: null,
    onlyFit: false,
    observer: null,
    scheduled: false,
  };

  const NEED_FEATURES = Object.freeze({
    digestion: ['trawienie', 'problemy gastryczne', 'wrażliwe trawienie', 'jelita'],
    skin: ['skóra i sierść', 'skóra/sierść'],
    weight: ['kontrola wagi', 'kontrola masy'],
    joints: ['stawy', 'ruchliwość'],
    energy: ['energia', 'aktywność'],
    teeth: ['zęby', 'jama ustna'],
    development: ['rozwój'],
    ageing: ['starzenie', 'senior'],
  });

  function split(value) {
    return String(value || '')
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function norm(value) {
    return String(value || '').trim().toLocaleLowerCase('pl-PL');
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function petName() {
    return state.pet?.name || 'pupila';
  }

  function customAnimals(values) {
    const out = new Set();
    for (const raw of values) {
      const value = norm(raw);
      if (value.includes('dla psa')) out.add('dog');
      if (value.includes('dla kota')) out.add('cat');
    }
    return [...out];
  }

  function customLifeStages(values) {
    const out = new Set();
    for (const raw of values) {
      const value = norm(raw);
      if (value.includes('w każdym wieku')) out.add('all-stages');
      if (value.includes('doros')) out.add('adult');
      if (value.includes('senior')) out.add('senior');
      if (value.includes('szczen')) out.add('puppy');
      if (value.includes('koci')) out.add('kitten');
      if (value.includes('junior')) out.add('junior');
    }
    return [...out];
  }

  function customSizes(values) {
    const out = new Set();
    for (const raw of values) {
      const value = norm(raw);
      if (value.includes('wszystkich ras')) out.add('all-sizes');
      if (value.includes('mini')) out.add('mini');
      if (value.includes('mała')) out.add('small');
      if (value.includes('średnia')) out.add('medium');
      if (value.includes('duża')) out.add('large');
      if (value.includes('olbrzym')) out.add('giant');
    }
    return [...out];
  }

  function metaFromDataset(dataset) {
    const taxonomyAnimal = norm(dataset.zpAnimal);
    const customAnimalValues = customAnimals(split(dataset.zpCustomAnimal));
    const animals = customAnimalValues.length
      ? customAnimalValues
      : taxonomyAnimal === 'multi-pet'
        ? ['dog', 'cat']
        : taxonomyAnimal
          ? [taxonomyAnimal]
          : [];

    const customStages = customLifeStages(split(dataset.zpCustomLife));
    const taxonomyStage = norm(dataset.zpLifeStage);
    const lifeStages = customStages.length ? customStages : (taxonomyStage ? [taxonomyStage] : []);

    const customSizeValues = customSizes(split(dataset.zpCustomSize));
    const taxonomySize = norm(dataset.zpSize);
    const sizes = customSizeValues.length ? customSizeValues : (taxonomySize ? [taxonomySize] : []);

    return {
      animals,
      category: norm(dataset.zpCategory),
      subcategory: norm(dataset.zpSubcategory),
      lifeStages,
      sizes,
      features: split(dataset.zpFeatures).map(norm),
      flavours: split(dataset.zpMainFlavour).map(norm),
    };
  }

  function metaFromCard(card) {
    return metaFromDataset(card.dataset);
  }

  function metaFromProductHost(host) {
    return metaFromDataset(host.dataset);
  }

  function productIsFood(meta) {
    return meta.category === 'food' || meta.subcategory.includes('food');
  }

  function productIsVeterinaryFood(meta) {
    return meta.subcategory === 'veterinary-food';
  }

  function productIsIngestible(meta) {
    return productIsFood(meta) || meta.subcategory === 'treats' || meta.subcategory === 'supplements';
  }

  function lifeStageCompatibility(pet, meta) {
    if (!productIsFood(meta)) return { kind: 'ignore' };
    if (!meta.lifeStages.length) return { kind: 'unknown', reason: 'brak informacji o wieku na produkcie' };
    if (meta.lifeStages.includes('all-stages')) return { kind: 'match' };
    if (pet.lifeStage === 'unknown') return { kind: 'unknown', reason: 'wiek pupila jest nieznany' };

    let allowed = [];
    if (pet.lifeStage === 'young') {
      allowed = pet.species === 'cat' ? ['kitten', 'junior'] : ['puppy', 'junior'];
    } else if (pet.lifeStage === 'adult') {
      allowed = ['adult'];
    } else if (pet.lifeStage === 'senior') {
      allowed = ['senior'];
    }

    if (meta.lifeStages.some((stage) => allowed.includes(stage))) return { kind: 'match' };
    return { kind: 'mismatch', reason: 'inny etap życia' };
  }

  function sizeCompatibility(pet, meta) {
    if (pet.species !== 'dog') return { kind: 'ignore' };
    if (!meta.sizes.length) return { kind: 'ignore' };
    if (meta.sizes.includes('all-sizes')) return { kind: 'match' };
    if (pet.size === 'unknown') return { kind: 'unknown', reason: 'wielkość psa jest nieznana' };

    const allowed = {
      small: ['mini', 'small'],
      medium: ['medium'],
      large: ['large'],
    }[pet.size] || [];

    if (meta.sizes.some((size) => allowed.includes(size))) return { kind: 'match' };
    return { kind: 'mismatch', reason: 'inna wielkość psa' };
  }

  const EXCLUSION_FLAVOURS = Object.freeze({
    chicken: ['kurczak'],
    beef: ['wołowina'],
    lamb: ['jagnięcina'],
    fish: ['ryba', 'ryby', 'łosoś', 'dorsz', 'tuńczyk', 'pstrąg', 'sardynka', 'makrela', 'śledź'],
  });

  function declaredHardExclusions(pet) {
    const values = Array.isArray(pet?.declaredExclusions) ? pet.declaredExclusions : [];
    return values.filter((value) => !['none', 'unknown', 'other'].includes(value));
  }

  function explicitRestrictionConflict(pet, meta) {
    if (!productIsIngestible(meta)) return null;
    const exclusions = declaredHardExclusions(pet);
    if (!exclusions.length) return null;

    for (const exclusion of exclusions) {
      const needles = EXCLUSION_FLAVOURS[exclusion] || [];
      if (needles.some((needle) => meta.flavours.some((flavour) => flavour.includes(needle)))) {
        return exclusion;
      }
    }
    return null;
  }

  function restrictionsPositivelySatisfied(pet, meta) {
    if (!productIsIngestible(meta)) return true;

    const req = pet?.dietaryRequirements || {};
    const declared = Array.isArray(pet?.declaredExclusions) ? pet.declaredExclusions : [];
    if (req.exclusionStatus === 'none' || declared.includes('none') || declared.length === 0) return true;
    if (req.exclusionStatus === 'unknown' || declared.includes('unknown')) return false;

    const exclusions = declaredHardExclusions(pet);
    const hasCustom = declared.includes('other') || Boolean(req.otherExclusionText);
    if (hasCustom) return false;

    // Current catalog has a structured "Bezzbożowa" signal. Use it positively.
    // Protein exclusions need a structured ingredient/free-from source before we award ✓.
    const grainOnly = exclusions.length === 1 && exclusions[0] === 'grain';
    if (grainOnly) {
      return meta.features.some((feature) => feature.includes('bezzboż'));
    }

    return exclusions.length === 0;
  }

  function exclusionSummary(pet) {
    const req = pet?.dietaryRequirements || {};
    if (req.exclusionStatus === 'none' || pet?.declaredExclusions?.includes('none')) return null;
    if (req.exclusionStatus === 'unknown' || pet?.declaredExclusions?.includes('unknown')) return 'wykluczenia nie są określone';
    const labels = {
      chicken: 'kurczak', beef: 'wołowina', lamb: 'jagnięcina', fish: 'ryby', grain: 'zboża', other: 'inne składniki'
    };
    const values = Array.isArray(pet?.declaredExclusions)
      ? pet.declaredExclusions.filter((x) => !['none', 'unknown'].includes(x)).map((x) => labels[x] || x)
      : [];
    if (req.otherExclusionText) values.push(req.otherExclusionText);
    return values.length ? `wykluczenia: ${values.join(', ')}` : null;
  }

  function matchedNeed(pet, meta) {
    const needs = pet?.dietaryRequirements?.declaredNeeds;
    if (!Array.isArray(needs) || !needs.length || !meta.features.length) return null;
    for (const need of needs) {
      const needles = NEED_FEATURES[need] || [];
      if (needles.some((needle) => meta.features.some((feature) => feature.includes(needle)))) return need;
    }
    return null;
  }

  function classify(pet, meta) {
    if (!pet) return { status: 'none', headline: '', detail: '' };

    if (!meta.animals.length) {
      return { status: 'check', headline: `Sprawdź dla ${pet.name}`, detail: 'Brak jednoznacznej informacji, dla jakiego zwierzęcia jest produkt.' };
    }
    if (!meta.animals.includes(pet.species)) {
      return { status: 'no', headline: `Nie pasuje do ${pet.name}`, detail: pet.species === 'cat' ? 'Produkt jest przeznaczony dla innego zwierzęcia.' : 'Produkt jest przeznaczony dla innego zwierzęcia.' };
    }

    const life = lifeStageCompatibility(pet, meta);
    if (life.kind === 'mismatch') {
      return { status: 'no', headline: `Nie pasuje do ${pet.name}`, detail: `Profil i produkt mają ${life.reason}.` };
    }

    const size = sizeCompatibility(pet, meta);
    if (size.kind === 'mismatch') {
      return { status: 'no', headline: `Nie pasuje do ${pet.name}`, detail: `Profil i produkt mają ${size.reason}.` };
    }

    const req = pet.dietaryRequirements || {};
    if (productIsFood(meta) && req.veterinaryDietStatus === 'yes') {
      if (!productIsVeterinaryFood(meta)) {
        return { status: 'no', headline: `Nie pasuje do ${pet.name}`, detail: `${pet.name} ma zadeklarowaną dietę weterynaryjną. Zwykłej karmy nie oznaczamy jako zgodnej.` };
      }
      return { status: 'check', headline: `Sprawdź dla ${pet.name}`, detail: 'To karma weterynaryjna, ale sklep nie ma jeszcze ustrukturyzowanej kategorii leczniczej tego produktu. Sprawdź zgodność z zaleceniem lekarza.' };
    }

    if (productIsFood(meta) && req.veterinaryDietStatus === 'unknown') {
      return { status: 'check', headline: '', detail: 'Brak deklaracji dotyczącej diety weterynaryjnej.' };
    }

    if (productIsIngestible(meta)) {
      const conflict = explicitRestrictionConflict(pet, meta);
      if (conflict) {
        return { status: 'no', headline: '', detail: 'Produkt ma jawny konflikt z zadeklarowanym wykluczeniem.' };
      }

      if (!restrictionsPositivelySatisfied(pet, meta)) {
        return { status: 'check', headline: '', detail: 'Brak wystarczająco mocnego pozytywnego potwierdzenia dla ograniczeń żywieniowych.' };
      }
    }

    // Brak części danych nie jest sam w sobie konfliktem.
    // Nie dajemy wtedy ✓, ale taki produkt może pozostać w profilu-aware widoku.
    if (life.kind === 'unknown') {
      return { status: 'check', headline: '', detail: `Brak pełnego potwierdzenia wieku: ${life.reason}.` };
    }
    if (size.kind === 'unknown') {
      return { status: 'check', headline: '', detail: `Brak pełnego potwierdzenia rozmiaru: ${size.reason}.` };
    }

    const need = matchedNeed(pet, meta);
    const needLabels = {
      digestion: 'trawienie', skin: 'skórę i sierść', weight: 'kontrolę masy', joints: 'stawy', energy: 'aktywność', teeth: 'zęby', development: 'rozwój', ageing: 'seniora'
    };
    return {
      status: 'fit',
      headline: `Pasuje do ${pet.name}`,
      detail: need ? `Zgodne z profilem i dodatkowo oznaczone pod kątem: ${needLabels[need] || need}.` : 'Zgodne z dostępnymi, ustrukturyzowanymi danymi profilu i produktu.'
    };
  }

  function ensureStyle() {
    if (document.getElementById('zp-pet-fit-v1-style')) return;
    const style = document.createElement('style');
    style.id = 'zp-pet-fit-v1-style';
    style.textContent = `
      .zp-fit-badge{display:inline-flex;align-items:center;gap:5px;width:max-content;max-width:100%;min-height:25px;padding:4px 8px;border-radius:999px;font-size:11px;font-weight:760;line-height:1.2;margin:5px 0 1px;position:relative;z-index:3}
      .zp-fit-badge--fit{background:#e7f3ec;color:#15513a}
      .zp-fit-toolbar{grid-column:1/-1;box-sizing:border-box;display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 0 16px;padding:12px 14px;border:1px solid rgba(18,63,46,.14);border-radius:16px;background:#f7faf8;color:#123f2e}
      .zp-fit-toolbar__copy{display:flex;align-items:center;gap:9px;min-width:0}.zp-fit-toolbar__pet{width:34px;height:34px;display:grid;place-items:center;border-radius:50%;background:#fff;font-size:18px;flex:none}.zp-fit-toolbar__text{display:grid;gap:1px;min-width:0}.zp-fit-toolbar__text strong{font-size:.92rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.zp-fit-toolbar__text span{color:#65736c;font-size:.78rem}
      .zp-fit-toolbar__toggle{appearance:none;display:inline-flex;align-items:center;justify-content:center;min-height:38px;padding:8px 12px;border:1px solid #123f2e;border-radius:10px;background:#fff;color:#123f2e;font:inherit;font-size:.85rem;font-weight:760;cursor:pointer;white-space:nowrap}.zp-fit-toolbar__toggle[aria-pressed="true"]{background:#123f2e;color:#fff}
      .zp-fit-hidden{display:none!important}.zp-fit-pdp{box-sizing:border-box;margin:0 0 14px;padding:13px 14px;border-radius:14px;border:1px solid rgba(18,63,46,.14);background:#f7faf8;color:#123f2e;display:grid;gap:6px}.zp-fit-pdp--fit{border-color:rgba(21,81,58,.28);background:#f1f8f4}.zp-fit-pdp__top{display:flex;align-items:center;gap:8px}.zp-fit-pdp__top strong{font-size:.95rem}.zp-fit-pdp__detail{font-size:.84rem;line-height:1.4;color:#65736c}.zp-fit-pdp__actions{display:flex;align-items:center;gap:12px;flex-wrap:wrap}.zp-fit-pdp__switch,.zp-fit-pdp__profile{appearance:none;border:0;background:transparent;padding:0;color:#123f2e;text-decoration:underline;font:inherit;font-size:.8rem;font-weight:700;cursor:pointer;width:max-content}.zp-fit-pdp__profile{text-decoration:none}.zp-fit-pdp__profile:hover{text-decoration:underline}
      .zp-fit-mobile-context{display:none;position:fixed;z-index:2147481200;left:12px;bottom:78px;max-width:calc(100vw - 24px);min-height:40px;padding:6px 11px;border:1px solid rgba(18,63,46,.18);border-radius:999px;background:#fff;color:#123f2e;box-shadow:0 10px 28px rgba(18,63,46,.15);font:inherit;font-size:.82rem;font-weight:780;align-items:center;gap:7px;cursor:pointer}.zp-fit-mobile-context span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.zp-fit-manage-shop{margin-top:8px;display:inline-flex!important}
      @media(max-width:989px){.zp-fit-mobile-context{display:inline-flex}}
      @media(max-width:749px){.zp-fit-toolbar{align-items:stretch;flex-direction:column}.zp-fit-toolbar__toggle{width:100%}.zp-fit-badge{font-size:10px;padding:4px 7px}.zp-fit-mobile-context{bottom:72px}.product-card .zp-fit-badge{margin-top:4px}}
    `;
    document.head.append(style);
  }

  function badgeLabel(result) {
    return result.status === 'fit' ? `✓ ${result.headline}` : '';
  }

  function renderCard(card) {
    card.querySelector(':scope > .product-card__content .zp-fit-badge')?.remove();
    const item = card.closest(GRID_ITEM_SELECTOR);
    if (item) item.classList.remove('zp-fit-hidden');
    delete card.dataset.zpFitStatus;
    if (!state.pet) return null;

    const result = classify(state.pet, metaFromCard(card));
    card.dataset.zpFitStatus = result.status;
    const content = card.querySelector(':scope > .product-card__content');
    if (content && result.status === 'fit') {
      const badge = document.createElement('div');
      badge.className = 'zp-fit-badge zp-fit-badge--fit';
      badge.textContent = badgeLabel(result);
      badge.title = result.detail;
      badge.setAttribute('aria-label', `${badgeLabel(result)}. ${result.detail}`);
      const title = content.querySelector('[ref="productTitleLink"]');
      const chips = content.querySelector('.zpc-purpose-chips');
      if (chips) chips.before(badge);
      else if (title) title.insertAdjacentElement('afterend', badge);
      else content.append(badge);
    }

    if (state.onlyFit && item && result.status === 'no') item.classList.add('zp-fit-hidden');
    return result.status;
  }

  function ensureToolbar(grid, counts) {
    let toolbar = grid.parentElement?.querySelector(':scope > .zp-fit-toolbar');
    if (!state.pet) {
      toolbar?.remove();
      return;
    }
    if (!toolbar) {
      toolbar = document.createElement('div');
      toolbar.className = 'zp-fit-toolbar';
      grid.before(toolbar);
    }
    const icon = state.pet.species === 'cat' ? '🐈' : '🐕';
    toolbar.innerHTML = `
      <div class="zp-fit-toolbar__copy">
        <div class="zp-fit-toolbar__pet">${icon}</div>
        <div class="zp-fit-toolbar__text"><strong>Dla ${escapeHTML(state.pet.name)}</strong><span>${counts.fit} oznaczonych ✓ · filtr ukrywa tylko jawne konflikty · <a href="/pages/moje-zwierzaki" style="color:inherit;font-weight:750">Zobacz profil</a></span></div>
      </div>
      <button type="button" class="zp-fit-toolbar__toggle" data-zp-fit-only aria-pressed="${state.onlyFit ? 'true' : 'false'}">${state.onlyFit ? 'Pokaż wszystkie' : `Dopasuj widok do ${escapeHTML(state.pet.name)}`}</button>`;
  }

  function renderGrid(grid) {
    const counts = { fit: 0, check: 0, no: 0 };
    for (const item of grid.querySelectorAll(GRID_ITEM_SELECTOR)) {
      const card = item.querySelector(CARD_SELECTOR);
      if (!card) continue;
      const status = renderCard(card);
      if (status && own(counts, status)) counts[status] += 1;
    }
    ensureToolbar(grid, counts);
  }

  function own(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function renderLooseCards() {
    for (const card of document.querySelectorAll(`${CARD_SELECTOR}:not(.product-grid ${CARD_SELECTOR})`)) renderCard(card);
  }

  function renderPdp() {
    document.querySelectorAll('.zp-fit-pdp').forEach((node) => node.remove());
    if (!state.pet) return;
    const hosts = [...document.querySelectorAll(META_SELECTOR)];
    if (!hosts.length) return;
    for (const host of hosts) {
      const section = host.closest('.shopify-section') || document;
      const details = section.querySelector('.product-details .group-block') || section.querySelector('.product-details');
      if (!details) continue;
      const result = classify(state.pet, metaFromProductHost(host));
      if (result.status !== 'fit') continue;
      const box = document.createElement('div');
      box.className = 'zp-fit-pdp zp-fit-pdp--fit';
      box.innerHTML = `<div class="zp-fit-pdp__top"><span>✓</span><strong>${escapeHTML(result.headline)}</strong></div><div class="zp-fit-pdp__detail">${escapeHTML(result.detail)}</div><div class="zp-fit-pdp__actions"><a class="zp-fit-pdp__profile" href="/pages/moje-zwierzaki">Zobacz profil ${escapeHTML(state.pet.name)}</a><button type="button" class="zp-fit-pdp__switch" data-zp-fit-switch-pet>Zmień pupila</button></div>`;
      details.insertBefore(box, details.firstChild);
    }
  }

  function ensureMobileContext() {
    let button = document.querySelector('[data-zp-fit-mobile-context]');
    if (!state.pet) {
      button?.remove();
      return;
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'zp-fit-mobile-context';
      button.setAttribute('data-zp-fit-mobile-context', '');
      document.body.append(button);
    }
    button.innerHTML = `<span>${state.pet.species === 'cat' ? '🐈' : '🐕'}</span><span>Kupuję dla: ${escapeHTML(state.pet.name)}</span><span>▾</span>`;
  }

  function enhanceManagePage() {
    const host = document.querySelector(MANAGE_PAGE_SELECTOR);
    if (!host || !state.pet) return;
    const card = [...host.querySelectorAll('.zp-petcard')].find((node) => node.classList.contains('is-active'));
    if (!card || card.querySelector('[data-zp-fit-shop-pet]')) return;
    const actions = card.querySelector('.zp-petcard__actions');
    if (!actions) return;
    const link = document.createElement('a');
    link.className = 'zp-petcenter__button zp-fit-manage-shop';
    link.setAttribute('data-zp-fit-shop-pet', '');
    link.href = `/collections/${state.pet.species === 'cat' ? 'koty' : 'psy'}`;
    link.textContent = `Zobacz produkty dla ${state.pet.name}`;
    actions.prepend(link);
  }

  function applyAutoFilterFromUrl() {
    state.onlyFit = false;
    sessionStorage.removeItem(FILTER_KEY);

    const url = new URL(location.href);
    if (url.searchParams.has('zp_fit')) {
      url.searchParams.delete('zp_fit');
      history.replaceState(history.state, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  }

  function renderAll() {
    ensureStyle();
    for (const grid of document.querySelectorAll('ul.product-grid[data-testid="product-grid"]')) renderGrid(grid);
    renderLooseCards();
    renderPdp();
    ensureMobileContext();
    enhanceManagePage();
  }

  function scheduleRender() {
    if (state.scheduled) return;
    state.scheduled = true;
    requestAnimationFrame(() => {
      state.scheduled = false;
      renderAll();
    });
  }

  function readActivePetFromApi() {
    try {
      const api = window.ZooplicityPetProfiles;
      if (!api?.getActivePet) return null;
      return api.getActivePet();
    } catch (_) {
      return null;
    }
  }

  function openPetSwitcher() {
    const headerButton = document.querySelector('[data-zp-header-pet]');
    if (headerButton instanceof HTMLElement) {
      headerButton.click();
      return;
    }
    location.assign('/pages/moje-zwierzaki');
  }

  function bindEvents() {
    window.addEventListener(PET_EVENT, (event) => {
      state.pet = event.detail?.activePet || null;
      scheduleRender();
    });
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-zp-fit-only]')) {
        event.preventDefault();
        event.stopPropagation();
        state.onlyFit = !state.onlyFit;
        sessionStorage.removeItem(FILTER_KEY);
        renderAll();
        return;
      }
      if (target.closest('[data-zp-fit-mobile-context], [data-zp-fit-switch-pet]')) {
        event.preventDefault();
        event.stopPropagation();
        openPetSwitcher();
      }
    }, true);
  }

  function mutationNodeTouchesProducts(node) {
    if (!(node instanceof Element)) return false;

    if (node.matches('.zp-fit-toolbar, .zp-fit-pdp, .zp-fit-mobile-context, .zp-fit-badge, .zp-fit-manage-shop')) {
      return false;
    }
    if (node.closest('.zp-fit-toolbar, .zp-fit-pdp, .zp-fit-mobile-context, .zp-fit-badge')) {
      return false;
    }

    if (node.matches(CARD_SELECTOR, GRID_ITEM_SELECTOR, META_SELECTOR, MANAGE_PAGE_SELECTOR)) {
      return true;
    }

    return Boolean(
      node.querySelector?.(
        `${CARD_SELECTOR}, ${GRID_ITEM_SELECTOR}, ${META_SELECTOR}, ${MANAGE_PAGE_SELECTOR}`
      )
    );
  }

  function observe() {
    if (state.observer) return;
    state.observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        return nodes.some(mutationNodeTouchesProducts);
      });
      if (relevant) scheduleRender();
    });

    const host = document.querySelector('#MainContent') || document.body;
    if (host) state.observer.observe(host, { childList: true, subtree: true });
  }

  function boot() {
    applyAutoFilterFromUrl();
    state.pet = readActivePetFromApi();
    bindEvents();
    observe();
    renderAll();
    setTimeout(() => {
      const pet = readActivePetFromApi();
      if (pet) state.pet = pet;
      renderAll();
    }, 800);
  }

  window.ZooplicityPetFit = Object.freeze({
    classify: (pet, meta) => classify(pet, meta),
    refresh: () => { state.pet = readActivePetFromApi(); renderAll(); },
    getState: () => ({ activePet: state.pet ? JSON.parse(JSON.stringify(state.pet)) : null, onlyFit: state.onlyFit }),
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
